import { useState } from 'react';
import type { ScheduleValue } from '@/features/admin/ui/ToolConfigPage/scheduleValue';
import {
  useRemoveWorkspaceOpportunitySchedule,
  useUpdateWorkspaceOpportunitySchedule,
  useWorkspaceOpportunitySchedules,
} from './useOpportunitySchedule';

const EMPTY_SCHEDULE: ScheduleValue = { daysOfWeek: [], enabled: false };

interface Draft {
  identity: string;
  schedule: ScheduleValue;
  generationEndDate: string | null;
}

/**
 * Editing state for one workspace's run schedule, kept separate from the save so a surface that
 * saves several things at once can decide when the schedule goes with them.
 *
 * `exists` is false for a workspace with no opportunities job at all, which has no schedule to
 * override and would fail the save.
 *
 * The generation-end-date cutoff is orthogonal to schedule override and is edited/saved alongside
 * it via the same mutation. Draft state resets on any server change to keep a stale refetch from
 * masking someone else's write.
 */
export const useWorkspaceScheduleOverride = (workspaceId: string) => {
  const { data: schedules, isLoading } = useWorkspaceOpportunitySchedules();
  const updateMutation = useUpdateWorkspaceOpportunitySchedule();
  const removeMutation = useRemoveWorkspaceOpportunitySchedule();

  const schedule = schedules?.find((entry) => entry.workspaceId === workspaceId);
  const effective: ScheduleValue = schedule
    ? { daysOfWeek: schedule.daysOfWeek, enabled: schedule.enabled }
    : EMPTY_SCHEDULE;
  const effectiveGenerationEndDate: string | null = schedule?.generationEndDate ?? null;

  // Local edits, discarded whenever the server value changes identity. Keyed by the inherit /
  // override flip and the values themselves so a refetch after saving does not keep showing the
  // pre-save draft.
  const identity = `${schedule?.hasOverride ?? false}|${effective.enabled}|${effective.daysOfWeek.join()}|${effectiveGenerationEndDate ?? ''}`;
  const [draft, setDraft] = useState<Draft | null>(null);
  const active = draft?.identity === identity ? draft : null;
  const value = active?.schedule ?? effective;
  const generationEndDate = active ? active.generationEndDate : effectiveGenerationEndDate;

  const updateDraft = (next: Partial<Pick<Draft, 'schedule' | 'generationEndDate'>>) =>
    setDraft({
      identity,
      schedule: next.schedule ?? value,
      generationEndDate:
        next.generationEndDate !== undefined ? next.generationEndDate : generationEndDate,
    });

  const cutoffDirty = generationEndDate !== effectiveGenerationEndDate;

  return {
    isLoading,
    exists: schedule !== undefined,
    hasOverride: schedule?.hasOverride ?? false,
    value,
    generationEndDate,
    /** What the server holds, for anything that should report state rather than follow an edit. */
    saved: effective,
    savedGenerationEndDate: effectiveGenerationEndDate,
    setValue: (next: ScheduleValue) => updateDraft({ schedule: next }),
    setGenerationEndDate: (next: string | null) => updateDraft({ generationEndDate: next }),
    isDirty:
      value.enabled !== effective.enabled ||
      value.daysOfWeek.join() !== effective.daysOfWeek.join() ||
      cutoffDirty,
    save: () =>
      updateMutation.mutateAsync({
        ...value,
        workspaceId,
        generationEndDate: cutoffDirty ? generationEndDate : undefined,
      }),
    reset: () => removeMutation.mutateAsync(workspaceId),
    isSaving: updateMutation.isPending,
    isResetting: removeMutation.isPending,
  };
};
